import { Component, EventEmitter, Host, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { cloneDeep, filter, sortBy } from 'lodash';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { StudentNote } from '../../classroom360/student-notes/student-note.model';
import { FamilyRegistrationService } from '../../shared-modules/family-registration/family-registration.service';
import { NewFamilyRegistrationYearSelectionComponent } from '../../shared-modules/family-registration/new-family-registration-year-selection/new-family-registration-year-selection.component';
import { OnlineRegistrationService } from '../../student360/online-registration/online-registration.service';
import { StudentDetailsComponent } from '../../student360/student-details/student-details.component';
import { StudentNotesPopupComponent } from '../../student360/student-summary/student-notes-popup/student-notes-popup.component';
import { StudentSummaryService } from '../../student360/student-summary/student-summary.service';
import { Student360Service } from '../../student360/student360.service';
import { CustomLayoutsService } from '../custom-layouts/custom-layouts.service';
import { CustomLayoutType } from '../custom-layouts/Models/custom-layout-type.model';
import { CustomLayout } from '../custom-layouts/Models/custom-layout.model';
import { PermissionEnum } from '../security/permissions.enums';
import { SecurityService } from '../security/security.service';
import { Enrollment } from '../student/enrollment.model';
import { Student } from '../student/student.model';
import { StudentService } from '../student/student.service';
import { System } from '../System';
import { UserService } from '../user/user.service';
import { Year } from '../year/year.model';
import { YearService } from '../year/year.service';
import { LoginService } from './../../shared-modules/login/login.service';
import { Alerts } from './../student/student.model';
import { StudentImageDialogComponent } from './student-image-dialog/student-image-dialog.component';

@AutoUnsubscribe()
@Component({
    selector: 'student-header',
    templateUrl: './student-header.component.html',
    styleUrls: [
        './student-header.component.css'
    ]
})
export class StudentHeaderComponent implements OnInit, OnDestroy {

    // Interface
    @HostBinding('class.sis-app-root') sisAppRoot: Host = true;

    @Input() showStudent = true;
    @Input() useParentPortalYear = false; // Use the defaultParentPortalYear rather than the currently selected year.
    @Input() showYearSelector: boolean; // shows or hides the year selector
    @Input() showAllYearOption: boolean;
    @Input() onlineRegistration: boolean;
    @Input() onlinePayment: boolean;
    @Input() parentPortalUpdate: boolean;
    @Input() disableYearSelect: boolean; // shows the year selector but it is disabled
    @Input() showCustomLayouts: boolean;
    @Input() disableNavigation: boolean;	// Removes the ability to change students on the header
    @Input() pageTitle: string; // Blue title that displays on the header
    @Input() useYear: Year;
    @Input() courseRequestYear: Year;
    @Input() allowFutureYears = false;
    @Input() useStudents: Student[];
    @Input() noEnrollmentString = 'Not enrolled in this school year - Please select another year';
    @Input() useStudentName = '';
    @Input() showAYLabel = false;
    @Input() hideAlerts = false;
    @Input() showEnrollmentDates = false;
    @Input() stepNumber: number;

    @Output() studentChange = new EventEmitter<number>();
    @Output() useYearChange = new EventEmitter<Year>();
    @Input() eForm: boolean;

    // Custom Layouts
    selectedLayoutId: number;
    customLayouts: CustomLayout[];
    ViewlayoutTypes: CustomLayoutType[] = [];
    selectedViewTypeId: number;
    selectedLayoutTypeName = '';

    // Students
    private _studentId: number;
    selectedStudent: Student;
    studentEnrollment: Enrollment;
    studentAlerts: Alerts[];

    // Years
    years: Year[];
    selectedYear: Year;
    defaultYear: Year;

    // Other
    subscriptions: Subscription[] = [];
    V10: boolean;
    showReEnrollButton = false;
    instanceName = '';

    ReEnrollYears: Year[] = [];
    frYearsDialogRef: MatDialogRef<NewFamilyRegistrationYearSelectionComponent>;
    medicalShowAlertSubscriptions: Subscription;

    showDetailsButton: boolean;
    hasPermission: boolean;

    // Class Functions
    constructor(
        private studentService: StudentService,
        private yearService: YearService,
        private customLayoutService: CustomLayoutsService,
        private userService: UserService,
        private router: Router,
        private onlineRegistrationService: OnlineRegistrationService,
        private loginService: LoginService,
        private student360Service: Student360Service,
        private dialog: MatDialog,
        private familyRegistrationService: FamilyRegistrationService,
        private studentSummaryService: StudentSummaryService,
        private securityService: SecurityService,


    ) {
        this.medicalShowAlertSubscriptions = this.studentService.observableMedicalShowAlertStream$.subscribe(() => {
            this.getStudentAlerts();
        });

        this.showDetailsButton = this.student360Service.showDetailsButtonOnStudentHeader;
    }

    get students(): Student[] {
        if (this.useStudents) {
            return this.useStudents;
        } else {
            return this.studentService.students;
        }
    }

    get totalStudents(): number {
        return this.students ? this.students.length : 0;
    }

    get studentId(): number {
        return this._studentId;
    }

    get userType(): number {
        return this.userService.currentUser.UserType;
    }

    @Input() set studentId(value: number) {
        if (this._studentId != value) {
            this._studentId = value;
            if (this._studentId) {
                this.loadSelectedStudent();
            }
        }
    }

    get showAlerts() {
        return (this.userType == 1 || !this.V10) && !this.hideAlerts;
    }

    // Event Functions
    ngOnInit() {
        // Setting intial properties
        this.V10 = this.loginService.LoginSettings.V10;
        this.instanceName = this.loginService.LoginSettings.InstanceName;

        // Setting up custom layouts
        this.subscriptions.push(
            this.customLayoutService.observableSelectedLayoutStream$.subscribe(selectedLayout => {
                this.selectedLayoutId = selectedLayout.Id;
            })
        );
        this.subscriptions.push(
            this.customLayoutService.observableSelectedLayoutViewTypeStream$.subscribe(selectedLayout => {
                this.selectedLayoutTypeName = this.customLayoutService.selectedLayoutType.Name.replace('Academic History', '');
            })
        );
        this.subscriptions.push(
            this.customLayoutService.observableSavedLayoutsStream$.subscribe(savedLayouts => {
                this.customLayouts = savedLayouts;
                this.ViewlayoutTypes = this.customLayoutService.layoutTypes.filter(x => x.Name.indexOf(this.pageTitle) > -1);
                this.selectedViewTypeId = this.customLayoutService.selectedLayoutType.Id;
                this.selectedLayoutTypeName = this.customLayoutService.selectedLayoutType.Name.replace('Academic History', '');
            })
        );
        this.subscriptions.push(
            this.yearService.observableYearStream$.subscribe(years => {
                this.getYears();
            })
        );
        // Obtaining the years for the selector
        this.getYears();

        // We respond to selected student unless we're using a custom list of students
        if (!this.useStudents) {
            this.subscriptions.push(
                this.studentService.selectedStudentStream$.subscribe(studentId => {
                    if (this.studentService.selectedStudent) {
                        this.studentId = this.studentService.selectedStudent.Id; // studentId;
                        if (this.students != null && this.students.findIndex(o => o.Id == this.studentService.selectedStudent.Id) < 0) {
                            this.students.push(this.studentService.selectedStudent);
                        }
                    }
                })
            );
        }

        // If we are using a custom set of students, then we want to be sure to capture all student changes
        if (this.useStudents) {
            if (!this.studentId) {
                this.studentId = this.useStudents[0].Id;
            }
        }

        // Loading the student immediately if we have it
        if (this.studentId && !this.selectedStudent) {
            this.loadSelectedStudent();
        }

        // Responding to year change
        this.subscriptions.push(
            this.yearService.observableStudent360YearStream$.subscribe(() => {
                this.selectedYear = this.yearService.student360Year;
            })
        );

        this.subscriptions.push(
            this.student360Service.observableParentAssignedPortalsStream$.subscribe(data => {
                if (this.router.url.toLowerCase().indexOf('/student360') >= 0) {
                    let portalURL = this.router.url.toLowerCase().replace('/student360', '');
                    if (portalURL.includes('?')) {
                        portalURL = portalURL.substring(0, portalURL.indexOf('?'));
                    }
                    const parentAssignedPortal = data.filter(a => a.PortalDescription.toLowerCase() == portalURL && a.Disabled == true)[0];
                    if (parentAssignedPortal != null) {
                        this.router.navigateByUrl('/student360/studentSummary');
                    }
                    else {
                        this.setPortalYear();
                    }
                }
            })
        );
        if (this.useParentPortalYear) {
            // Get the default year
            if (this.userType == 1) {
                // Because the header is the the same for parents, students and staff
                // invert this option for staff
                this.useParentPortalYear = false;
            } else {
                this.defaultYear = this.yearService.allYears.filter(y => y.Default)[0];
            }
        }

        if (this.securityService.IsAnyPermissionCodeAssigned('SD_VFD', 'SD_MFD')) {
            this.hasPermission = true;
        }
    }

    getStudentNotes() {
        // Determine wheather the user has permission to get StudentNotes
        if (this.V10) {

            if (this.securityService.IsAnyPermissionAssigned(PermissionEnum.StudentNotesCounselorView, PermissionEnum.StudentNotesMyStudentsView, PermissionEnum.StudentNotesStudentRecordView)) {
                this.subscriptions.push(forkJoin([this.studentSummaryService.getStudentNoteCategories(this.yearService.student360Year.SchoolID, this.yearService.student360Year.Id),
                this.studentSummaryService.getStudentNotesForPopup(this.selectedStudent.Id, this.yearService.student360Year.SchoolID, this.yearService.student360Year.Id)]).subscribe(result => {
                    if (result) {
                        const notesToDisplay: StudentNote[] = [];
                        const categories = result[0];
                        const notes = result[1];
                        const popupCategories = sortBy(filter(categories, cat => cat.StudentNotesPopup), cat => cat.DisplayOrder);
                        popupCategories.forEach(cat => {
                            const popupNotes = sortBy(notes.filter(note => note.SNCID == cat.Id), n => n.Order);
                            popupNotes.forEach(note => {
                                if (cat.OncePerSession && this.studentSummaryService.acknowledgedStudentNotes.filter(n => n.Id == note.Id).length == 0) {
                                    notesToDisplay.push(note);
                                    this.studentSummaryService.acknowledgedStudentNotes.push(note);
                                } else if (!cat.OncePerSession) {
                                    notesToDisplay.push(note);
                                }
                            });
                        });
                        if (notesToDisplay.length > 0) {
                            const dialogRef = this.dialog.open(StudentNotesPopupComponent,
                                {
                                    disableClose: true,
                                    data: sortBy(notesToDisplay, n => n.Order)
                                });
                        }
                    }
                }));
            }
        } else {
            this.subscriptions.push(
                this.userService.getUserPermissions(
                    this.yearService.defaultYear.Id,
                    this.yearService.defaultYear.SchoolID
                ).subscribe(permissions => {
                    const userPermissions = permissions;
                    if (userPermissions.find(permission => permission.PermissionName == 'View Student Notes') || userPermissions.find(permission => permission.PermissionName == 'Maintain Student Notes')) {
                        this.subscriptions.push(forkJoin([this.studentSummaryService.getStudentNoteCategories(this.yearService.student360Year.SchoolID, this.yearService.student360Year.Id),
                        this.studentSummaryService.getStudentNotesForPopup(this.selectedStudent.Id, this.yearService.student360Year.SchoolID, this.yearService.student360Year.Id)]).subscribe(result => {
                            if (result) {
                                const notesToDisplay: StudentNote[] = [];
                                const categories = result[0];
                                const notes = result[1];
                                const popupCategories = sortBy(filter(categories, cat => cat.StudentNotesPopup), cat => cat.DisplayOrder);
                                popupCategories.forEach(cat => {
                                    const popupNotes = sortBy(notes.filter(note => note.SNCID == cat.Id), n => n.Order);
                                    popupNotes.forEach(note => {
                                        if (cat.OncePerSession && this.studentSummaryService.acknowledgedStudentNotes.filter(n => n.Id == note.Id).length == 0) {
                                            notesToDisplay.push(note);
                                            this.studentSummaryService.acknowledgedStudentNotes.push(note);
                                        } else if (!cat.OncePerSession) {
                                            notesToDisplay.push(note);
                                        }
                                    });
                                });
                                if (notesToDisplay.length > 0) {
                                    const dialogRef = this.dialog.open(StudentNotesPopupComponent,
                                        {
                                            disableClose: true,
                                            data: sortBy(notesToDisplay, n => n.Order)
                                        });
                                }
                            }
                        }));
                    }
                }));
        }
    }

    ngOnDestroy() {
        // POST: unsubscribes to all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    yearChanged(yearId: number) {
        // PRE: the year changed from the dropdown
        // POST: sets the year and emits a year change
        if (this.useYear) {
            this.useYear = this.years.find(year => year.Id == yearId);
            this.useYearChange.emit(this.useYear);
        } else {
            const year = this.years.filter(y => y.Id == yearId)[0];
            this.yearService.updateStudent360Year(year);
            this.useYearChange.emit(year);
        }
        if (!this.useStudents) {    // Only update enrollment if we are using shared students
            this.updateStudentEnrollment();
        }
    }

    // Methods
    selectedLayoutChange() {
        const l = this.customLayouts.find(o => o.Id == this.selectedLayoutId);
        if (this.customLayoutService.selectedLayoutType.Id != l.LayoutTypeID) {
            this.selectedViewTypeId = l.LayoutTypeID;
            this.selectedLayoutTypeName = this.ViewlayoutTypes.filter(t => t.Id == this.selectedViewTypeId)[0].Name.replace('Academic History', '');
            this.selectedViewTypeChange();
        }
        this.customLayoutService.selectedLayout = System.copyObject(l, new CustomLayout());
    }

    selectedViewTypeChange() {
        const l = this.ViewlayoutTypes.find(o => o.Id == this.selectedViewTypeId);
        this.selectedLayoutTypeName = this.ViewlayoutTypes.filter(t => t.Id == this.selectedViewTypeId)[0].Name.replace('Academic History', '');
        this.customLayoutService.selectedLayoutViewType = System.copyObject(l, new CustomLayoutType());
    }

    loadSelectedStudent() {
        if (this.showStudent) {

            // If a student ID is provided and we have a list of students, use that
            if (this.studentId && this.students) {
                const selectedStudent = this.students.find(student => student.Id == this.studentId);
                if (selectedStudent) {
                    this.selectedStudent = selectedStudent;
                    this.getStudentPhoto();
                    this.studentService.getStudentEnrollmentYears(this.studentId).pipe(take(1)).subscribe(enrollmentYears => {
                        this.studentService.enrollmentYears = enrollmentYears;
                        if (this.selectedYear) {
                            this.studentService.getStudentEnrollment(this.studentId, this.selectedYear.Id).pipe(take(1)).subscribe(enrollment => {
                                this.studentEnrollment = enrollment;
                                this.studentService.studentEnrollment = enrollment;
                                const url = this.router.url.toLowerCase();
                                if (url.indexOf('/student360/onlineregistration') < 0 && url.indexOf('/student360/studentfields') < 0 && url.indexOf('registrationenrollment') < 0 && (this.studentEnrollment == null && this.selectedYear.Id > 0)) {
                                    this.router.navigateByUrl('/student360/studentSummary');
                                }
                                this.getYears();
                                this.studentChange.emit(this.studentId);
                                // We need to set the portal year manually on V9
                                if (!this.V10) {
                                    this.setPortalYear();
                                }
                                this.getStudentReEnrollStatus();
                            });
                            if (this.showAlerts) {
                                this.getStudentAlerts();
                            }
                            if (this.userService.currentUser.UserType == 1) {
                                this.getStudentNotes();
                            }
                        }
                    });
                } else {
                    this.selectedStudent = new Student();
                }
            }

            // If not student ID is provided or the user has no students, use the student in the student service
            else {
                this.selectedStudent = this.studentService.selectedStudent;
                this.getStudentPhoto();
                this.studentEnrollment = this.studentService.studentEnrollment;
                if (this.showAlerts && this.selectedStudent) {
                    this.getStudentAlerts();
                }
                if (this.userService.currentUser.UserType == 1) {
                    this.getStudentNotes();
                }
                this.getYears();

                // We need to set the portal year manually on V9
                if (!this.V10) {
                    this.setPortalYear();
                }
                this.getStudentReEnrollStatus();
            }
        }
    }

    getStudentAlerts() {
        this.subscriptions.push(this.studentService.getAlerts([this.selectedStudent.Id]).subscribe(alerts => {
            this.studentAlerts = alerts;
            this.studentChange.emit(this.studentId);
        }));
    }

    getStudentPhoto() {
        // We don't always have the photo for students.  If the selected student doesnt' have one, then we need to go get it.
        if (this.selectedStudent && (this.selectedStudent.Picture == null || this.selectedStudent.Picture.length == 0)) {
            const year = this.yearService.defaultYear ? this.yearService.defaultYear : this.yearService.student360Year;
            if (year) {
                this.studentService.getStudentPicture(this.selectedStudent.Id, year.SiteYearId).subscribe(pic => {
                    this.selectedStudent.Picture = pic;
                });
            }
        }
    }

    getStudentReEnrollStatus() {
        this.showReEnrollButton = false;

        if (this.V10 == false && this.userType == 2 && this.selectedStudent != null) {
            this.familyRegistrationService.getORYears(this.selectedYear.Id, this.selectedStudent.Id).subscribe(students => {
                if (students.length > 0 && this.router.url.toLowerCase() != '/student360/eforms') {
                    this.showReEnrollButton = true;
                }
            });
        }
    }

    getYears() {
        const url = this.router.url.toLowerCase();
        if (!this.allowFutureYears || this.V10) {
            if (!this.onlinePayment) {
                if (this.router.url.toLowerCase() != '/student360/graduationplan' && this.router.url.toLowerCase() != '/student360/transportation' && this.router.url.toLowerCase() != '/student360/courserequests' && this.router.url.toLowerCase() != '/student360/courserequest' && this.router.url.toLowerCase().indexOf('student360/onlinepayment') < 0 ) {
                    if (this.useYear) {
                        if (this.V10) {
                            this.years = cloneDeep(this.studentService.enrollmentYears);
                        }
                        else {
                            this.years = cloneDeep(this.yearService.years.filter(o => o.SchoolID == this.useYear.SchoolID));
                            if (this.years.length == 0) {
                                this.years = cloneDeep(this.yearService.studentYears);
                            }
                        }
                    } else {
                        if (this.V10) {
                            this.years = cloneDeep(this.studentService.enrollmentYears);
                        }
                        else {
                            if (this.userService.currentUser.UserType == 1 && this.showYearSelector) {
                                this.years = cloneDeep(this.yearService.studentYears);
                            }
                            else if (this.userService.currentUser.UserType == 1 && !this.showYearSelector) {
                                this.years = cloneDeep(this.yearService.yearsForC360);
                            }
                            else {
                                this.years = cloneDeep(this.yearService.allYears.filter(o => o.SchoolID == this.yearService.student360Year.SchoolID));
                            }
                        }
                        if (url.indexOf('/student360/teams') > -1 || url.indexOf('/student360/behavior') > -1 || url.indexOf('/student360/awards') > -1) {
                            const allYear = new Year();
                            allYear.Description = '(All)';
                            allYear.Id = -1;
                            allYear.SchoolID = this.yearService.student360Year.SchoolID;
                            if (this.years) {
                                this.years.unshift(allYear);
                            }
                        }
                    }
                } 
                else {
                    if (this.useYear) {
                        this.years = this.yearService.years.filter(o => o.SchoolID == this.useYear.SchoolID);
                    } else {
                        this.years = this.yearService.years.filter(o => o.SchoolID == this.yearService.student360Year.SchoolID);
                    }
                }
            }
            else {
                if (this.useYear) {
                    this.years = cloneDeep(this.yearService.years.filter(o => o.SchoolID == this.useYear.SchoolID && o.EnableOnlinePayment));
                } else {
                    this.years = cloneDeep(this.yearService.years.filter(o => o.SchoolID == this.yearService.student360Year.SchoolID && o.EnableOnlinePayment));
                }
            }
            this.setSelectedYear();
        }
        else {
            if (this.studentEnrollment) {
                this.yearService.getAllEnrolledYears(this.studentEnrollment.StudentId, this.studentEnrollment.SchoolId).subscribe(results => {
                    this.years = results;
                    this.setSelectedYear();
                });
            }
            else if (this.onlineRegistration) {
                this.years = cloneDeep(this.yearService.years);
                this.selectedYear = this.onlineRegistrationService.ORYear;
            }
        }
    }

    setSelectedYear() {
        if (this.useYear) {
            if (this.showAYLabel) {
                this.selectedYear = this.useYear;
            }
            else {
                this.selectedYear = this.years.find(o => o.Id == this.useYear.Id);
                if (!this.selectedYear && this.allowFutureYears) {
                    this.selectedYear = this.years.find(o => o.AYIdentifier == this.useYear.AYIdentifier);
                }
                // ONE-7485 && Fixed Again for ONE-7727
                else if (!this.selectedYear && (this.userService.studentInformationView || this.router.url.indexOf('/classroom360/sendEmail') > -1)) {
                    this.selectedYear = this.years.filter(o => o.AYIdentifier == this.useYear.AYIdentifier)[0];
                    this.useYear = this.selectedYear;
                }
            }
        }
        else if (this.onlineRegistration) {
            this.selectedYear = this.years.find(o => o.Id == this.onlineRegistrationService.ORYear.Id);
        } else if (this.useParentPortalYear) {
            this.selectedYear = this.yearService.allYears.filter(y => y.Default)[0];
            this.updateStudentEnrollment();
        }
        else if (this.V10 == false && this.courseRequestYear != undefined && this.courseRequestYear != null) {
            this.selectedYear = this.courseRequestYear;
        }
        else {
            if (this.yearService.student360Year.Id == -1) {
                if (this.V10 && this.years.filter(y => y.Id == -1).length == 0) {
                    this.selectedYear = this.yearService.years.filter(y => y.Default)[0];
                    this.yearService.student360Year = this.selectedYear;
                }
                else if (this.years && this.years.filter(y => y.Id == -1).length == 0) {
                    this.selectedYear = this.years.filter(y => y.Default)[0];
                    this.yearService.student360Year = this.selectedYear;
                }
            }
            if (this.V10) {
                this.selectedYear = (this.years) ? this.years.filter(y => y.AYIdentifier == this.yearService.student360Year.AYIdentifier)[0] : this.yearService.student360Year;
            }
            else {
                this.selectedYear = this.yearService.student360Year;
            }
        }
    }

    setPortalYear() {
        // NOTE: V9 only
        if (this.student360Service.parentAssignedPortals != null) {
            let currentUrl = this.router.url;
            if (currentUrl.toLowerCase() != '/student360/graduationplan' &&
                currentUrl.toLowerCase() != '/student360/transportation' &&
                currentUrl.toLowerCase() != '/student360/onlineregistration' &&
                currentUrl.toLowerCase() != '/student360/courserequests' &&
                currentUrl.toLowerCase() != '/student360/courserequest' &&
                currentUrl.toLowerCase().indexOf('/student360/onlinepayment') < 0) {
                if (currentUrl.toLowerCase() == '/student360/setnotificationpreferences') {
                    currentUrl = '/student360/notificationpreferences';
                }

                const academicYearAttention = this.student360Service.parentAssignedPortals.filter(o => o.PortalDescription != null && o.PortalDescription != '' && currentUrl.toLowerCase().includes('/student360' + o.PortalDescription.toLowerCase()) && o.Disabled == false)[0];
                if (academicYearAttention != undefined && this.years) {
                    this.years.forEach((element, i) => {
                        switch (element.Status) {
                            case 0:
                                if (academicYearAttention.ShowonPastYear == false) {
                                    this.years[i].Disabled = true;
                                }
                                break;
                            case 1:
                                if (academicYearAttention.ShowonCurrentYear == false) {
                                    this.years[i].Disabled = true;
                                }
                                break;
                            case 2:
                                if (academicYearAttention.ShowonFutureYear == false) {
                                    this.years[i].Disabled = true;
                                }
                                break;
                        }
                    });
                }
            } else if (currentUrl.toLowerCase().indexOf('/student360/onlinepayment') > -1) {
                this.years.forEach((element) => {
                    if (element.EnableOnlinePayment == false) {
                        element.Disabled = true;
                    }
                    if (element.EnableOnlinePayment == true) {
                        const paymentYear = this.student360Service.parentAssignedPortals.filter(x => x.PortalName == 'Meal Service' || x.PortalName == 'Fees & Billing')[0];
                        switch (element.Status) {
                            case 0:
                                if (paymentYear.ShowonPastYear == false) {
                                    element.Disabled = true;
                                }
                                break;
                            case 1:
                                if (paymentYear.ShowonCurrentYear == false) {
                                    element.Disabled = true;
                                }
                                break;
                            case 2:
                                if (paymentYear.ShowonFutureYear == false) {
                                    element.Disabled = true;
                                }
                                break;
                        }
                    }
                });
            }
        }
    }

    getStudentSuffix(): string {
        const suffix = this.selectedStudent.Suffix;
        if (suffix != null && suffix.length > 0) {
            const numeralMatch = /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$|^\d+$/;
            if (suffix.match(numeralMatch)) {
                return ' ' + suffix;
            } else {
                return ', ' + suffix;
            }
        }
        return '';
    }

    studentImageClicked(image) {
        // Open a modal with a larger student image
        this.dialog.open(StudentImageDialogComponent, { data: { studentImage: image }, disableClose: true, autoFocus: false });
    }

    onRightClick($event: MouseEvent) {
        return false;
    }

    updateStudentEnrollment() {
        if (this.selectedStudent && this.selectedYear && this.selectedYear.Id > 0) {
            this.studentService.getStudentEnrollment(this.selectedStudent.Id, this.selectedYear.Id).subscribe(enrollment => {
                this.studentEnrollment = enrollment;
                if (!this.useParentPortalYear) { // if using defaultParentPortalYear we dont want to update the enrollment in the service
                    this.studentService.studentEnrollment = enrollment;
                }
                // We need to set the portal year manually on V9
                if (!this.V10) {
                    this.setPortalYear();
                }
                this.getStudentReEnrollStatus();
            });
        }
    }

    reEnrollStudentClick() {
        this.familyRegistrationService.getORYears(0, this.selectedStudent.Id).subscribe(reEnrollYears => {
            this.ReEnrollYears = reEnrollYears;
            if (this.ReEnrollYears.length > 0) {
                this.frYearsDialogRef = this.dialog.open(NewFamilyRegistrationYearSelectionComponent, {
                    disableClose: true,
                    maxWidth: '500px',
                    data: {
                        years: this.ReEnrollYears,
                        reEnrollStudent: true,
                        familyId: this.selectedStudent.FamilyId
                    }
                });
            }
        });
    }

    viewStudentDetails() {
        this.student360Service.showDetailsButtonOnStudentHeader = false;
        const dialogRef = this.dialog.open(StudentDetailsComponent, {
            height: '100vh',
            width: '100vw',
            maxWidth: '100vw',
            // autoFocus: false,
            disableClose: true,
            data: {
                isOverlay: true
            }
        });

        dialogRef.afterClosed().subscribe(res => {
            this.student360Service.showDetailsButtonOnStudentHeader = true;
        });
    }
}
